import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ChevronRight, ChevronLeft, X, Target, Brain, Heart, 
  Sparkles, CheckCircle2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const slides = [
  {
    id: 1,
    title: "Welcome to Brioritizer",
    subtitle: "Focus on what truly matters",
    content: "Brioritizer helps you understand and prioritize what's in your best interests — for your wellbeing, the people you care about, and your future.",
    icon: Sparkles,
    color: "from-emerald-500 to-teal-500"
  },
  {
    id: 2,
    title: "What are Interests?",
    subtitle: "More than just tasks",
    content: "Interests are anything that affects your wellbeing: work goals, personal care, relationships, finances, health, hobbies — even protecting yourself from stress or threats. They're what truly matter to you.",
    icon: Heart,
    color: "from-rose-500 to-pink-500"
  },
  {
    id: 3,
    title: "The Attention Axis",
    subtitle: "Your mental & emotional energy",
    content: "Attention isn't just time — it's your mental capacity, emotional energy, and the effort required. Consider deadlines, skills needed, and barriers to overcome when deciding how much attention an interest needs.",
    icon: Brain,
    color: "from-violet-500 to-purple-500"
  },
  {
    id: 4,
    title: "The Impact Axis",
    subtitle: "Your control & influence",
    content: "Impact measures how much control you have and whether your actions can make a real difference. Focus on interests where you can create positive change or prevent negative outcomes.",
    icon: Target,
    color: "from-amber-500 to-orange-500"
  },
  {
    id: 5,
    title: "The Four Quadrants",
    subtitle: "Prioritize with purpose",
    content: null, // Custom content for this slide
    icon: null,
    color: "from-emerald-500 to-teal-500",
    isQuadrantSlide: true
  }
];

const QuadrantExplanation = () => (
  <div className="grid grid-cols-2 gap-3 mt-4">
    <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <ArrowUpRight className="w-4 h-4 text-emerald-500" />
        <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">High Attention / High Impact</span>
      </div>
      <p className="text-xs text-muted-foreground">Your focus zone. Focus your energy here — these are interests you can meaningfully influence.</p>
    </div>
    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <ArrowUpRight className="w-4 h-4 text-amber-500" />
        <span className="font-semibold text-amber-600 dark:text-amber-400 text-sm">High Attention / Low Impact</span>
      </div>
      <p className="text-xs text-muted-foreground">Caution zone. You're spending energy on things largely outside your control. Consider if it's worth the stress.</p>
    </div>
    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <ArrowDownRight className="w-4 h-4 text-amber-500" />
        <span className="font-semibold text-amber-600 dark:text-amber-400 text-sm">Low Attention / High Impact</span>
      </div>
      <p className="text-xs text-muted-foreground">Opportunity zone. These matter and you can influence them — consider giving them more attention.</p>
    </div>
    <div className="bg-rose-500/20 border border-rose-500/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <ArrowDownRight className="w-4 h-4 text-rose-500" />
        <span className="font-semibold text-rose-600 dark:text-rose-400 text-sm">Low Attention / Low Impact</span>
      </div>
      <p className="text-xs text-muted-foreground">Release zone. These aren't in your control and aren't getting attention. Consider letting go, accepting them, or waiting until they require more attention.</p>
    </div>
  </div>
);

export const WelcomeModal = ({ isOpen, onClose, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('welcome_modal_seen', 'true');
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('welcome_modal_seen', 'true');
    onClose();
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && handleSkip()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-lg bg-background rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-muted">
              <motion.div
                className={`h-full bg-gradient-to-r ${slide.color}`}
                initial={{ width: 0 }}
                animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Close button */}
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Content */}
            <div className="p-6 pt-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="min-h-[320px]"
                >
                  {/* Icon */}
                  {Icon && (
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${slide.color} flex items-center justify-center mb-4 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  )}

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-center mb-1">{slide.title}</h2>
                  <p className={`text-center text-sm font-medium bg-gradient-to-r ${slide.color} bg-clip-text text-transparent mb-4`}>
                    {slide.subtitle}
                  </p>

                  {/* Content */}
                  {slide.isQuadrantSlide ? (
                    <QuadrantExplanation />
                  ) : (
                    <p className="text-center text-muted-foreground leading-relaxed">
                      {slide.content}
                    </p>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Slide indicators */}
              <div className="flex justify-center gap-2 mt-6 mb-4">
                {slides.map((_, idx) => (
                  <button
                    key={`slide-${idx}`}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentSlide 
                        ? 'w-6 bg-primary' 
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between gap-4">
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  disabled={currentSlide === 0}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>

                <Button
                  onClick={handleNext}
                  className={`gap-1 bg-gradient-to-r ${slide.color} hover:opacity-90`}
                >
                  {currentSlide === slides.length - 1 ? (
                    <>
                      Get Started
                      <CheckCircle2 className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
